// Préparation des écritures du DSI (POST /corrections). Règles de l'API : simulation d'abord, If-Match pour
// modifier, valider et retirer, motif obligatoire pour retirer, jamais de suppression.
import type { Changement, Correction, Lien, Objet } from '../api/types';
import { valeurLisible } from './format';

export type NatureValeur = 'booleen' | 'nombre' | 'texte' | 'montant' | 'autre';

export function natureDe(v: unknown): NatureValeur {
  if (typeof v === 'boolean') return 'booleen';
  if (typeof v === 'number') return 'nombre';
  if (typeof v === 'string') return 'texte';
  if (v && typeof v === 'object' && 'valeur' in v) return 'montant';
  if (v === null || v === undefined) return 'texte';
  return 'autre';
}

/** Saisie du formulaire → valeur JSON. « inconnu » (ou vide pour un booléen) donne null, comme dans le pivot. */
export function lireSaisie(nature: NatureValeur, texte: string, ancienne?: unknown): unknown {
  const t = texte.trim();
  if (t.toLowerCase() === 'inconnu') return null;
  switch (nature) {
    case 'booleen':
      if (t === 'oui' || t === 'true') return true;
      if (t === 'non' || t === 'false') return false;
      return null;
    case 'nombre': {
      const n = Number(t.replace(/\s/g, '').replace(',', '.'));
      if (t === '' || Number.isNaN(n)) throw new Error(`nombre attendu : « ${texte} »`);
      return n;
    }
    case 'montant': {
      const n = Number(t.replace(/\s|€/g, '').replace(',', '.'));
      if (t === '' || Number.isNaN(n)) throw new Error(`montant attendu : « ${texte} »`);
      const base = (ancienne && typeof ancienne === 'object' ? ancienne : {}) as Record<string, unknown>;
      return { ...base, valeur: n, devise: 'EUR' };
    }
    case 'autre':
      try {
        return JSON.parse(t);
      } catch {
        throw new Error('valeur JSON attendue');
      }
    default:
      return t;
  }
}

/** Saisie initiale d'un champ à partir de la valeur actuelle. */
export function saisieDe(v: unknown): string {
  if (v === null) return 'inconnu';
  if (v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (typeof v === 'number' || typeof v === 'string') return String(v);
  if (typeof v === 'object' && 'valeur' in (v as object)) return String((v as { valeur: number }).valeur);
  return JSON.stringify(v);
}

/** Attributs réellement changés (comparaison JSON stricte). */
export function attributsChanges(avant: Record<string, unknown>, apres: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(apres)) {
    if (JSON.stringify(avant[k]) !== JSON.stringify(v)) out[k] = v;
  }
  return out;
}

export function correctionModifier(
  el: Objet | Lien,
  attributs: Record<string, unknown>,
  o: { nom?: string; motif?: string; auteur: string },
): Correction {
  const c: Correction = { operation: 'modifier', element: el.id, attributs, auteur: o.auteur };
  if (o.nom !== undefined && 'nom' in el && o.nom !== el.nom) c.nom = o.nom;
  if (o.motif?.trim()) c.motif = o.motif.trim();
  return c;
}

export function correctionValider(el: Objet | Lien, o: { motif?: string; auteur: string }): Correction {
  const c: Correction = { operation: 'valider', element: el.id, auteur: o.auteur };
  if (o.motif?.trim()) c.motif = o.motif.trim();
  return c;
}

/** Retrait : l'élément reste lisible et sort des calculs. Le motif est obligatoire. */
export function correctionRetirer(el: Objet | Lien, o: { motif: string; auteur: string }): Correction {
  if (!o.motif.trim()) throw new Error('un retrait exige un motif');
  return { operation: 'retirer', element: el.id, motif: o.motif.trim(), auteur: o.auteur };
}

export function slug(texte: string): string {
  return (
    texte
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'sans-nom'
  );
}

export interface SaisieTravaux {
  nom: string;
  description: string;
  cout: number | null;
  delaiJours: number | null;
  constat?: string;
}

/** Création d'un chantier (objet « travaux ») proposé par le DSI. */
export function correctionCreerTravaux(s: SaisieTravaux, o: { auteur: string; motif?: string }): Correction {
  const attributs: Record<string, unknown> = { description: s.description || null, etat: 'propose' };
  if (s.cout !== null) attributs.cout = { valeur: s.cout, devise: 'EUR', periode: 'unique' };
  if (s.delaiJours !== null) attributs.delai_jours = s.delaiJours;
  if (s.constat) attributs.constat = s.constat;
  const c: Correction = {
    operation: 'creer',
    contenu: { id: `travaux:${slug(s.nom)}`, type: 'travaux', nom: s.nom, attributs },
    auteur: o.auteur,
  };
  if (o.motif?.trim()) c.motif = o.motif.trim();
  return c;
}

/** Une différence de l'API rendue lisible : chemin sans l'identifiant en tête. */
export function cheminLisible(ch: Changement, element: string): string {
  return ch.chemin.startsWith(`${element}.`) ? ch.chemin.slice(element.length + 1) : ch.chemin;
}

const estFeuille = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  typeof v !== 'object' ||
  ('valeur' in (v as object) && typeof (v as { valeur: unknown }).valeur === 'number');

function feuilles(v: unknown, chemin: string): [string, unknown][] {
  if (estFeuille(v)) return [[chemin, v]];
  if (Array.isArray(v)) {
    if (v.length === 0) return [];
    if (v.every(estFeuille)) return [[chemin, v.map((x) => valeurLisible(x)).join(', ')]];
    return v.flatMap((x, i) => feuilles(x, `${chemin}.${i + 1}`));
  }
  return Object.entries(v as Record<string, unknown>).flatMap(([k, x]) => feuilles(x, `${chemin}.${k}`));
}

/**
 * Une création (ou un ajout de bloc) arrive comme un seul changement dont l'« après » est un objet entier : on le
 * déplie champ par champ, comme une modification (T6), au lieu de le montrer en JSON brut. Rien n'est retiré.
 */
export function aplatirChangement(ch: Changement): Changement[] {
  const vide = (x: unknown) => x === null || x === undefined;
  if (vide(ch.avant) && !estFeuille(ch.apres)) {
    const f = feuilles(ch.apres, ch.chemin);
    return f.length ? f.map(([chemin, apres]) => ({ ...ch, chemin, avant: null, apres })) : [ch];
  }
  if (vide(ch.apres) && !estFeuille(ch.avant)) {
    const f = feuilles(ch.avant, ch.chemin);
    return f.length ? f.map(([chemin, avant]) => ({ ...ch, chemin, avant, apres: null })) : [ch];
  }
  return [ch];
}

/** Les champs techniques de provenance changent à chaque écriture : on les montre à part, repliés. */
export function estTechnique(ch: Changement, element: string): boolean {
  const c = cheminLisible(ch, element);
  return c === 'version' || c.startsWith('provenance.') || c.startsWith('provenance_attributs.');
}

/**
 * Différence locale entre deux lectures d'un même élément (mêmes règles que l'API : feuille par feuille). Sert à
 * montrer, en cas de conflit (412), ce qui a changé depuis la lecture du DSI.
 */
export function differenceLocale(avant: unknown, apres: unknown, chemin = ''): Changement[] {
  const estObjet = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
  if (estObjet(avant) && estObjet(apres)) {
    const out: Changement[] = [];
    const cles = [...Object.keys(avant), ...Object.keys(apres).filter((k) => !(k in avant))];
    for (const k of cles) {
      const sous = chemin ? `${chemin}.${k}` : k;
      if (!(k in apres)) out.push({ chemin: sous, avant: avant[k], apres: null });
      else if (!(k in avant)) out.push({ chemin: sous, avant: null, apres: apres[k] });
      else out.push(...differenceLocale(avant[k], apres[k], sous));
    }
    return out;
  }
  return JSON.stringify(avant) === JSON.stringify(apres) ? [] : [{ chemin, avant, apres }];
}

export function libelleElement(el: Objet | Lien, nom: (id: string) => string): string {
  if ('source' in el) return `${nom(el.source)} → ${nom(el.cible)} (${el.type.replace(/_/g, ' ')})`;
  return el.nom;
}
